import { ShieldCheck, Server, Lock, EyeOff } from 'lucide-react'
import { ThemeToggle } from '@/components/ThemeToggle'
import {
  GlassPanel,
  GlowCard,
  GradientBadge,
  NumberMetric,
  Kicker,
  StatementBlock,
  VsRow,
  TrustBadgeRow,
  ParallaxSection,
} from '@/components/ui/brand'

interface SwatchProps {
  name: string
  varName: string
}

function Swatch({ name, varName }: SwatchProps) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="h-14 w-full rounded-lg border"
        style={{ background: `var(${varName})`, borderColor: 'var(--line)' }}
      />
      <div className="text-xs" style={{ color: 'var(--soft)' }}>
        {name}
      </div>
      <code className="text-[11px]" style={{ color: 'var(--muted-2)' }}>
        {varName}
      </code>
    </div>
  )
}

interface SectionProps {
  kicker: string
  title: string
  children: React.ReactNode
}

function Section({ kicker, title, children }: SectionProps) {
  return (
    <section className="mx-auto w-full max-w-6xl px-6 py-10">
      <Kicker>{kicker}</Kicker>
      <h2
        className="mb-6 mt-2 text-3xl font-extrabold tracking-tight"
        style={{ color: 'var(--text)' }}
      >
        {title}
      </h2>
      {children}
    </section>
  )
}

const SURFACE_TOKENS: SwatchProps[] = [
  { name: 'Hintergrund', varName: '--bg' },
  { name: 'Hintergrund 2', varName: '--bg-2' },
  { name: 'Panel', varName: '--panel' },
  { name: 'Panel 2', varName: '--panel-2' },
  { name: 'Linie', varName: '--line' },
]

const ACCENT_TOKENS: SwatchProps[] = [
  { name: 'Akzent (Orange/Blau)', varName: '--accent' },
  { name: 'Akzent 2 (hell/Teal)', varName: '--accent-2' },
  { name: 'Warnung · bald', varName: '--warn' },
  { name: 'Erfolg · Kosten', varName: '--success' },
  { name: 'Negativ · Wettbewerb', varName: '--danger' },
]

const BRAND_TOKENS: SwatchProps[] = [
  { name: 'Logo von', varName: '--logo-from' },
  { name: 'Logo bis', varName: '--logo-to' },
]

export default function StyleguidePage() {
  return (
    <div className="bunker-stage bunker-grid min-h-screen">
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 pt-10">
        <div>
          <Kicker>Design System</Kicker>
          <h1 className="mt-2 text-4xl font-extrabold tracking-tight" style={{ color: 'var(--text)' }}>
            bunker.ai · Dark-First
          </h1>
        </div>
        <ThemeToggle />
      </header>

      <Section kicker="Farben" title="Oberflächen">
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-5">
          {SURFACE_TOKENS.map((t) => (
            <Swatch key={t.varName} {...t} />
          ))}
        </div>
      </Section>

      <Section kicker="Farben" title="Funktionale Akzente">
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-4 md:grid-cols-7">
          {ACCENT_TOKENS.map((t) => (
            <Swatch key={t.varName} {...t} />
          ))}
        </div>
      </Section>

      <Section kicker="Farben" title="Marke & Verläufe">
        <div className="mb-6 grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-5">
          {BRAND_TOKENS.map((t) => (
            <Swatch key={t.varName} {...t} />
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex h-16 items-end rounded-lg p-2 text-xs" style={{ background: 'var(--grad-accent)', color: '#fff' }}>
            Akzent-Verlauf (orange / blau→teal)
          </div>
          <div className="flex h-16 items-end rounded-lg p-2 text-xs" style={{ background: 'var(--grad-logo)', color: '#fff' }}>
            Logo-Verlauf
          </div>
        </div>
      </Section>

      <Section kicker="Typografie" title="Schriftskala (Inter)">
        <div className="space-y-3" style={{ color: 'var(--text)' }}>
          <p className="text-6xl font-extrabold tracking-tight">KI, die Ihr Wissen kennt.</p>
          <p className="text-3xl font-bold tracking-tight" style={{ color: 'var(--soft)' }}>
            Souverän. Lokal. Belegbar.
          </p>
          <p className="text-lg" style={{ color: 'var(--muted-2)' }}>
            Fließtext: Ihre Daten verlassen niemals Ihr Haus.
          </p>
        </div>
      </Section>

      <Section kicker="Komponenten" title="GlowCard · GlassPanel">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <GlowCard glow="cyan">
            <div className="p-5">
              <h3 className="font-bold" style={{ color: 'var(--text)' }}>Akzent</h3>
              <p className="text-sm" style={{ color: 'var(--muted-2)' }}>Standard-Hervorhebung</p>
            </div>
          </GlowCard>
          <GlowCard glow="cyan" intensity="high">
            <div className="p-5">
              <h3 className="font-bold" style={{ color: 'var(--text)' }}>Akzent · stark</h3>
              <p className="text-sm" style={{ color: 'var(--muted-2)' }}>Statement / Hero</p>
            </div>
          </GlowCard>
          <GlowCard glow="green">
            <div className="p-5">
              <h3 className="font-bold" style={{ color: 'var(--text)' }}>Erfolg</h3>
              <p className="text-sm" style={{ color: 'var(--muted-2)' }}>Kosten & Beweis (sparsam)</p>
            </div>
          </GlowCard>
        </div>
      </Section>

      <Section kicker="Komponenten" title="Badges & Trust">
        <div className="mb-6 flex flex-wrap gap-3">
          <GradientBadge variant="cool">On-Premise</GradientBadge>
          <GradientBadge variant="cool">0 € Tokengebühren</GradientBadge>
          <GradientBadge variant="green">DSGVO-konform</GradientBadge>
          <GradientBadge variant="amber">Kommt bald</GradientBadge>
        </div>
        <TrustBadgeRow
          badges={[
            { label: 'On-Premise', icon: <Server size={14} aria-hidden="true" /> },
            { label: 'DSGVO-konform', icon: <ShieldCheck size={14} aria-hidden="true" /> },
            { label: 'Kein Cloud Act', icon: <Lock size={14} aria-hidden="true" /> },
            { label: 'Air-Gap-fähig', icon: <EyeOff size={14} aria-hidden="true" /> },
          ]}
        />
      </Section>

      <Section kicker="Komponenten" title="Kennzahlen">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          <NumberMetric value={0} suffix=" €" label="Tokengebühren" />
          <NumberMetric value={121} label="Automatisierte Tests" />
          <NumberMetric value={61} prefix="+" suffix=" %" label="Antwortgeschwindigkeit" />
          <NumberMetric value={17} label="Dateiformate" />
        </div>
      </Section>

      <Section kicker="Komponenten" title="Statement & Vergleich">
        <StatementBlock>
          „Präzision ist kein Zufall, sondern Architektur.“
        </StatementBlock>
        <div className="mt-6">
          <GlassPanel>
            <div className="divide-y" style={{ borderColor: 'var(--line)' }}>
              <VsRow feature="Antworten mit Seitenbeleg" them={false} us={true} />
              <VsRow feature="Eingebauter Faktencheck" them={false} us={true} />
              <VsRow feature="Daten verlassen das Haus" them={true} us={false} />
            </div>
          </GlassPanel>
        </div>
      </Section>

      <Section kicker="Komponenten" title="Parallax">
        <ParallaxSection speed={0.3}>
          <GlassPanel>
            <div className="p-10 text-center" style={{ color: 'var(--soft)' }}>
              Scrollen Sie — dieser Block bewegt sich mit Parallax-Effekt.
            </div>
          </GlassPanel>
        </ParallaxSection>
      </Section>

      <div className="h-24" />
    </div>
  )
}
